import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoginRequest } from '../types/auth';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { APP_ROUTES } from '../routing/appRoutes';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<LoginRequest>({
    username: '',
    password: '',
  });
  
  const returnToState = (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)
    ?.from;

  // Keep query-param support for full-page redirects (e.g. expired sessions).
  const searchParams = new URLSearchParams(window.location.search);
  const returnUrl = searchParams.get('returnUrl');

  const handleInputChange = (field: keyof LoginRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const user = await login(formData);
      
      // If there's a return URL, go there
      if (returnToState?.pathname) {
        navigate(`${returnToState.pathname}${returnToState.search || ''}${returnToState.hash || ''}`);
      } else if (returnUrl) {
        navigate(returnUrl);
      } else {
        // Otherwise, redirect based on user role
        if (user?.role === 'SUPERUSER' || user?.role === 'ADMIN' || user?.role === 'USER') {
          navigate(APP_ROUTES.company.dashboard);
        } else {
          navigate(APP_ROUTES.user.dashboard);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterClick = () => {
    navigate(APP_ROUTES.auth.register);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
        py: 3,
      }}
    >
      <Paper
        elevation={10}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 400,
          borderRadius: 2,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('auth.welcomeBack')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('auth.signInToAccount')}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label={t('auth.username', 'Username')}
            type="text"
            margin="normal"
            value={formData.username}
            onChange={(e) => handleInputChange('username', e.target.value)}
            autoComplete="username"
            autoFocus
            required
          />

          <TextField
            fullWidth
            label={t('auth.password')}
            type="password"
            margin="normal"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            autoComplete="current-password"
            required
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{ mt: 3, mb: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : t('auth.login')}
          </Button>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2">
              {t('auth.dontHaveAccount')}{' '}
              <Button
                variant="text"
                onClick={handleRegisterClick}
                sx={{ textTransform: 'none' }}
              >
                {t('auth.signUp')}
              </Button>
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};
